import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex items-center justify-between px-5 md:px-10 py-3.5">
          <Link to="/" className="flex flex-col flex-shrink-0">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 tracking-tight whitespace-nowrap">
              kakobuyfinds<span className="text-primary-500">.website</span>
            </span>
            <span className="text-[10px] text-foreground-500 tracking-wide">Kakobuy Spreadsheet 2026</span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line text-sm"></i>
              <span>{t('nav.home')}</span>
            </Link>

            <Link
              to="/poradnik-zakupowy-kakobuy"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line text-sm"></i>
              <span>{t('nav.guide')}</span>
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-menu-line text-base"></i>
                <span>{t('nav.categories')}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 flex items-center justify-between group whitespace-nowrap cursor-pointer"
                    >
                      <span>{t(cat.name)}</span>
                      <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('hero.cta_kakobuy')}
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <i className="ri-menu-line text-xl text-foreground-700"></i>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm"
            onClick={closeMobileMenu}
          ></div>

          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-sm bg-background-50 flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <Link to="/" onClick={closeMobileMenu} className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
                kakobuyfinds<span className="text-primary-500">.website</span>
              </Link>
              <button
                onClick={closeMobileMenu}
                className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-5">
              <div className="space-y-1 mb-6">
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-home-4-line text-lg text-primary-500"></i>
                  <span>{t('nav.home')}</span>
                </Link>
                <Link
                  to="/poradnik-zakupowy-kakobuy"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-book-open-line text-lg text-primary-500"></i>
                  <span>{t('nav.guide')}</span>
                </Link>
              </div>

              <div className="mb-4">
                <span className="px-4 text-xs font-semibold text-foreground-500 uppercase tracking-widest">
                  {t('nav.categories')}
                </span>
              </div>

              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-foreground-700 text-sm hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span>{t(cat.name)}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400"></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('hero.cta_kakobuy')}
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}